import randomNumber from "./randomNumber";
import {BooleanConditionallyUndefinedType, ConditionallyUndefinedType} from "./UtilityTypes";


/**
 * ObjectDataStore allows us to attach data of any object (non-native Object).
 * While this can be used on any object, it was created primarily to store data on objects.
 *
 * It largely pulls inspiration from the internal coding of `jQuery.data()`
 * but has been simplified for efficiency.
 * 
 * Create an instance of ObjectDataStore for each store purpose.
 * For example if you need to store data on an object for a component, you
 * only need to create one instance of this and use it over and over for each object.
 * @example
 * 		dataStore = new ObjectDataStore<any>();
 * 		dataStore.set(obj, storageKeyForThisTypeOfItem, valueToStore);
 */
export default class ObjectDataStore<EntryMap = {[key: string]: any}> {

	private static readonly expandoKeyPrefix = "_ObjectDataStore" + randomNumber(0, 100_000, true) + "_"; // TODO Add "version" to this like jQuery does.
	private static uid: number = 1;

	private readonly expandoKey: string;

	constructor() {
		this.expandoKey = ObjectDataStore.expandoKeyPrefix + ObjectDataStore.uid++;
	}

	private getCache<C extends boolean>(owner: object, create?: C): BooleanConditionallyUndefinedType<C, EntryMap>;
	private getCache(owner: object, create: boolean = false): EntryMap | undefined {
		if (this.hasCache(owner)) {
			return (owner as any)[this.expandoKey];
		}
		return create ? this.createStore(owner) : undefined;
	}

	private createStore(owner:object): EntryMap {
		const cache = Object.create(null);
		Object.defineProperty(
			owner,
			this.expandoKey,
			{
				value: cache,
				writable: true,
				configurable: true,
				enumerable: false
			}
		);
		return cache;
	}

	/**
	 * Returns true if `owner` has a store created on it.
	 */
	hasCache(owner:object): boolean {
		return owner.hasOwnProperty(this.expandoKey) && (owner as any)[this.expandoKey] !== undefined;
	}

	/**
	 * Check if there is a cache for the `owner` object and if it has any entries.
	 */
	hasData(owner:object): boolean {
		const cache = (owner as any)[this.expandoKey];
		return cache !== undefined && Object.keys(cache).length !== 0;
	}

	/**
	 * Set the `key` to the `value` on the cache for the `owner` object.
	 * Returns the `value` for convenience.
	 */
	set<K extends keyof EntryMap, V extends EntryMap[K]>(owner:object, key: K, value: V): EntryMap[K] {
		return this.getCache(owner, true)[key] = value;
	}

	/**
	 * Copy the properties of `data` one-by-one to the cache object for the `owner` object.
	 */
	setEach(owner:object, data:Partial<EntryMap>) {
		const cache = this.getCache(owner, true);
		Object.keys(data).forEach(prop => (cache as any)[prop] = (data as any)[prop]);
	}

	/**
	 * Get the value for the `key` in the cache object for the `owner` object.
	 * Returns defaultValue (which itself defaults to undefined) if no value was found for key.
	 */
	get<K extends keyof EntryMap, P extends EntryMap[K]>(owner:object, key: K, defaultValue?: P): ConditionallyUndefinedType<P, undefined, EntryMap[K]>;
	get<K extends keyof EntryMap>(owner:object, key: K, defaultValue?: EntryMap[K]): EntryMap[K] | undefined {
		return this.getCache(owner)?.[key] ?? defaultValue;
	}

	/**
	 * Returns all values for the object.
	 * Returns an empty array if no value was found for key.
	 */
	getAll(owner:object): any[] {
		const cache = this.getCache(owner, false);
		if (cache !== undefined) {
			// This is pseudo-polyfilling Object.values
			const resolveValues = ((Object as any).values as (o: any) => any[])
									?? ((o: any) => Object.getOwnPropertyNames(o).filter(o.propertyIsEnumerable).map(propertyName => o[propertyName]));
			return resolveValues(cache);
		}
		return [];
	}

	/**
	 * Returns a boolean indicating if the object has a value set for `key`.
	 * Returns false if the value is undefined or null.
	 */
	has<K extends keyof EntryMap>(owner:object, key: K): boolean {
		return this.getCache(owner)?.[key] !== undefined;
	}

	/**
	 * Remove the entry for the `key` in the cache object for the `owner` object.
	 * Returns the removed value, or undefined.
	 */
	remove<K extends keyof EntryMap>(owner:object, key: K): EntryMap[K] | undefined {
		const cache = this.getCache(owner);
		if (cache === undefined) {
			return;
		}

		const value = cache[key];
		delete cache[key];

		if (Object.keys(cache).length === 0) {
			this.clear(owner);
		}

		return value;
	}
	
	/**
	 * Clear the cache object for the `owner` object.
	 */
	clear(owner:object) {
		(owner as any)[this.expandoKey] = undefined; // Do not use 'delete' instead as it has issue in some browsers when used on DOM nodes.
	}
}
